import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import Layout from './Layout';
import apiClient from '../api/apiClient';
import axios from 'axios';
import progressService from '../services/progressService';

const EmailPasswordAuth = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('email'); // 'email', 'password'

  useEffect(() => {
    const initializeFromStorage = async () => {
      // Check if user already has data in localStorage
      const approvedEmail = localStorage.getItem('approved-email');
      const storedName = localStorage.getItem('applicant-name');
      const storedAppId = localStorage.getItem('application-id');
      
      if (approvedEmail && storedName && storedAppId) {
        setEmail(approvedEmail);
        setApplicantName(storedName);
        setApplicationId(storedAppId);
        
        // Check password status and update step
        try {
          const response = await apiClient.post('/check-password-status', {
            email: approvedEmail,
            application_id: parseInt(storedAppId)
          });

          if (response.data.status === 'success') {
            setHasPassword(response.data.data.has_password);
            setIsLoginMode(response.data.data.has_password);
            setStep('password');
          }
        } catch (err) {
          console.error('Error checking password status:', err);
          // If there's an error, stay on email step
          setStep('email');
        }
      }
    };

    initializeFromStorage();
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // DEBUG: Log API client configuration
    console.log('🔍 DEBUG: API Client base URL:', apiClient.defaults.baseURL);
    console.log('🔍 DEBUG: Making request to:', '/check-application-status');
    console.log('🔍 DEBUG: Full URL will be:', apiClient.defaults.baseURL + '/check-application-status');
    
    // Create a fresh axios instance to bypass caching
    const freshApiClient = axios.create({
      baseURL: 'https://api.segwaycapital.com/api',
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });
    
    console.log('🆕 FRESH API Client base URL:', freshApiClient.defaults.baseURL);
    
    try {
      const response = await freshApiClient.post('/check-application-status', {
        email: email
      });

      if (response.data.status === 'success') {
        const appData = response.data.data;
        
        // Check if the application is approved using the correct structure
        const isApproved = appData.isApproved || (appData.application && appData.application.status === 'approved');
        const application = appData.application || appData;
        
        if (isApproved) {
          // Save data to localStorage
          localStorage.setItem('approved-email', email);
          localStorage.setItem('applicant-name', application.name);
          localStorage.setItem('application-id', application.id.toString());
          
          setApplicantName(application.name);
          setApplicationId(application.id.toString());
          
          // Check if user has password
          try {
            const passwordResponse = await freshApiClient.post('/check-password-status', {
              email: email,
              application_id: application.id
            });

            if (passwordResponse.data.status === 'success') {
              setHasPassword(passwordResponse.data.data.has_password);
              setIsLoginMode(passwordResponse.data.data.has_password);
              setStep('password');
            }
          } catch (err) {
            console.error('Error checking password status:', err);
            setError('Error checking password status. Please try again.');
          }
        } else {
          setError('Your application is not yet approved. Please contact support.');
        }
      } else {
        setError(response.data.message || 'Application not found');
      }
    } catch (err) {
      console.error('Email submission error:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Create fresh API client for password submission too
    const freshApiClient = axios.create({
      baseURL: 'https://api.segwaycapital.com/api',
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });
    
    try {
      if (isLoginMode) {
        // Login with existing password
        const response = await freshApiClient.post('/investor-login', {
          email: email, 
          application_id: parseInt(applicationId),
          password: password 
        });

        if (response.data.status === 'success') {
          localStorage.setItem('registration-token', response.data.data.token);
          handleSuccessfulAuth();
        }
      } else {
        // Set up new password
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          return;
        }

        const response = await freshApiClient.post('/setup-investor-password', {
          email: email,
          application_id: parseInt(applicationId),
          password: password,
          password_confirmation: confirmPassword
        });

        if (response.data.status === 'success') {
          localStorage.setItem('registration-token', response.data.data.token);
          handleSuccessfulAuth();
        }
      }
    } catch (err) {
      console.error('Password submission error:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulAuth = async () => {
    try {
      // Update progress to mark password setup as complete
      await progressService.updateProgress(email, progressService.REGISTRATION_STEPS.PASSWORD_SETUP, {
        password_setup_complete: true
      });
      
      // Get exact current position for routing
      const exactPosition = await progressService.getExactCurrentPosition(email);
      
      console.log('✅ EmailPasswordAuth: User authenticated');
      console.log('📍 Routing to exact position:', exactPosition);
      
      navigate(exactPosition);
    } catch (error) {
      console.error('Error determining next step:', error);
      
      // Fallback to simple check based on localStorage
      const invType = localStorage.getItem('investment-type');
      const orgName = localStorage.getItem('organization-name');
      const entType = localStorage.getItem('entity-type');
      
      if (invType && orgName && entType) {
        console.log('🔄 Fallback: Routing to subscription (localStorage check)');
        navigate('/subscription');
      } else if (invType) {
        console.log('🔄 Fallback: Routing to investment type (localStorage check)');
        navigate('/investment-type');
      } else {
        console.log('🔄 Fallback: Routing to investment type (localStorage check)');
        navigate('/investment-type');
      }
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPassword('');
    setConfirmPassword('');
    setError('');
  };

  if (step === 'password') {
    return (
      <Layout>
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            p: { xs: 3, md: 4 },
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            maxWidth: '500px',
            margin: '0 auto',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 2,
              textAlign: 'center',
              color: '#1f2937',
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 600,
            }}
          >
            {isLoginMode ? 'Welcome Back!' : 'Set Up Your Password'}
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 4,
              textAlign: 'center',
              fontSize: '1.1rem',
              lineHeight: 1.6,
            }}
          >
            {isLoginMode 
              ? `Welcome back, ${applicantName}! Please enter your password to continue.`
              : `Hello ${applicantName}! Let's set up a secure password for your account.`
            }
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Card sx={{ mb: 4, backgroundColor: '#f8fdf8', border: '1px solid #d1fae5' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip 
                  label="APPROVED" 
                  color="success" 
                  sx={{ mr: 2, fontWeight: 600 }} 
                />
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                  Application Status
                </Typography>
              </Box>
              
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Name:</strong> {applicantName}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Email:</strong> {email}
              </Typography>
              <Typography variant="body1">
                <strong>Application ID:</strong> #{applicationId}
              </Typography>
            </CardContent>
          </Card>

          <Box component="form" onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
              sx={{ mb: 3 }}
            />

            {!isLoginMode && (
              <TextField
                fullWidth
                type="password"
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                required
                sx={{ mb: 3 }}
              />
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 500,
                mb: 2,
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                isLoginMode ? 'Sign In' : 'Set Password & Continue'
              )}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleBackToEmail}
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 500,
              }}
            >
              Back to Email
            </Button>
          </Box>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          p: { xs: 3, md: 4 },
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          margin: '0 auto',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 2,
            textAlign: 'center',
            color: '#1f2937',
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            fontWeight: 600,
          }}
        >
          Welcome to Segway Capital
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: 4,
            textAlign: 'center',
            fontSize: '1.1rem',
            lineHeight: 1.6,
          }}
        >
          Please enter the email address associated with your approved application to continue.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleEmailSubmit}>
          <TextField
            fullWidth
            type="email"
            label="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            required
            sx={{ mb: 4 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 500,
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Continue to Account Access'
            )}
          </Button>
        </Box>
      </Box>
    </Layout>
  );
};

export default EmailPasswordAuth; 